import { z } from "zod";

import type { JSON } from "~/tools/json";

import type { PersesPanelSpec } from "../openapi";

import { dashboardWidgetLinks } from "./_dashboard-widget-link";
import {
	type TimeSeriesChart,
	barChartSchema,
	gaugeChartSchema,
	markdownChartSchema,
	statChartSchema,
	timeSeriesChartSchema,
	timeSeriesQueries,
} from "./chart-schemas";

export const dashboardPluginSpecSchema = z.union([
	timeSeriesChartSchema,
	barChartSchema,
	statChartSchema,
	gaugeChartSchema,
	markdownChartSchema,
	z
		.object({
			queries: timeSeriesQueries,
			links: dashboardWidgetLinks,
		})
		.transform(
			({ queries, links }): TimeSeriesChart => ({
				kind: "TimeSeriesChart",
				spec: {},
				queries,
				links,
			}),
		),
]);

export type DashboardPluginSpec = z.infer<typeof dashboardPluginSpecSchema>;

export type DashboardPluginSpecKind = DashboardPluginSpec["kind"];

export const DASHBOARD_PLUGIN_KINDS = [
	"TimeSeriesChart",
	"BarChart",
	"StatChart",
	"GaugeChart",
	"Markdown",
] as const satisfies ReadonlyArray<DashboardPluginSpecKind>;

export function dashboardPluginSpecToPerses({ links, ...rest }: DashboardPluginSpec): PersesPanelSpec {
	const { queries, ...plugin } = { queries: "queries" in rest ? rest.queries : [], ...rest };

	return {
		plugin:
			plugin.kind === "Markdown"
				? {
						...plugin,
						spec: {
							...plugin.spec,
							text: plugin.spec.text?.source,
						},
					}
				: plugin,
		links: links.map((link) => ({
			...link,
			url: link.url.source,
			name: link.name?.source,
			tooltip: link.tooltip?.source,
		})),
		queries: queries.map(({ query, alias, minStep }) => ({
			kind: "TimeSeriesQuery",
			spec: {
				plugin: {
					kind: "PrometheusTimeSeriesQuery",
					spec: {
						query: query.source,
						alias: alias?.source,
						minStep: minStep?.source,
					},
				},
			},
		})),
	} satisfies JSON;
}
